import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { num } from '../lib/format.js';
import { EmptyState, ErrorState, Field, Panel, StatusBadge, Toast } from '../components/ui.js';
import '../screens-2a.css';
import '../screens-web.css';

/**
 * Hromadné zakládání webů ze souboru CSV (stovky webů najednou) a návod, jak na ně
 * nasadit měření jedním tagem v Google Tag Manageru. Zakládá jen správce uživatelů.
 */

interface PlanRow {
  line: number;
  name: string;
  seller: string;
  origins: string[];
  status: 'new' | 'exists';
}
interface ImportResult {
  applied: boolean;
  created: number;
  rows: PlanRow[];
  errors: Array<{ line: number; message: string }>;
}

const EXAMPLE =
  'name;seller;origins\nTickets GP;TGP;ticketsgp.example.cz shop.ticketsgp.example.cz\n';

const gtm = (
  base: string,
): string => `<!-- Google Tag Manager: tag „Hub – načtení“ (Vlastní HTML) -->
<!-- Spouštěč: po souhlasu návštěvníka s měřením -->
<script src="${base}/v1/web/loader.js" async></script>

<!-- Google Tag Manager: tag „Hub – souhlas“ (Vlastní HTML) -->
<!-- Pořadí: spustit až po tagu „Hub – načtení“ (Řazení tagů) -->
<script>window.hub && window.hub.consent(true);</script>`;

export function SitesImport(): JSX.Element {
  const client = useQueryClient();
  const [csv, setCsv] = useState('');
  const [copied, setCopied] = useState(false);
  const sites = useQuery<{ snippetBase: string }>({
    queryKey: ['sites'],
    queryFn: () => api.get('/api/sites'),
  });
  const check = useMutation({
    mutationFn: (apply: boolean) => api.post<ImportResult>('/api/sites/import', { csv, apply }),
    onSuccess: (result) => {
      if (result.applied) {
        void client.invalidateQueries({ queryKey: ['web-sites'] });
        void client.invalidateQueries({ queryKey: ['sites'] });
      }
    },
  });
  const result = check.data;
  const fresh = result?.rows.filter((r) => r.status === 'new').length ?? 0;
  const base = (sites.data?.snippetBase ?? '').replace(/\/v1\/web\/hub\.js$/, '');

  const copy = (): void => {
    void navigator.clipboard
      .writeText(gtm(base))
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2500);
      })
      .catch(() => setCopied(false));
  };

  return (
    <>
      <Panel
        title="Hromadné založení webů"
        subtitle="Vložte soubor CSV se sloupci name;seller;origins (název, značka, domény oddělené mezerou). Nové weby vzniknou vypnuté, zapnete je po kontrole."
      >
        <Field label="Soubor CSV" hint="Nejvýš 1000 řádků. Středník nebo čárka jako oddělovač.">
          <textarea
            rows={6}
            value={csv}
            placeholder={EXAMPLE}
            onChange={(e) => setCsv(e.target.value)}
            data-testid="sites-csv"
          />
        </Field>
        <div className="s2a-filters">
          <button
            type="button"
            className="btn"
            disabled={csv.trim() === '' || check.isPending}
            onClick={() => check.mutate(false)}
          >
            Zkontrolovat
          </button>
          <button
            type="button"
            className="btn pri"
            disabled={fresh === 0 || check.isPending || result?.applied === true}
            onClick={() => check.mutate(true)}
          >
            Založit nové weby ({num(fresh)})
          </button>
        </div>
        {check.isError ? (
          <ErrorState title="Soubor se nepodařilo zpracovat" error={check.error} />
        ) : null}
        {result?.applied ? (
          <Toast>Založeno {num(result.created)} webů, všechny vypnuté.</Toast>
        ) : null}
        {result ? (
          <>
            {result.errors.length > 0 ? (
              <ul className="note" data-testid="sites-csv-errors">
                {result.errors.map((e) => (
                  <li key={e.line}>
                    Řádek {e.line}: {e.message}
                  </li>
                ))}
              </ul>
            ) : null}
            {result.rows.length === 0 ? (
              <EmptyState title="V souboru není žádný platný řádek">
                Opravte chyby výše a zkontrolujte soubor znovu.
              </EmptyState>
            ) : (
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th className="n">Řádek</th>
                      <th>Web</th>
                      <th>Značka</th>
                      <th>Domény</th>
                      <th>Stav</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={r.line}>
                        <td className="n">{r.line}</td>
                        <td>{r.name}</td>
                        <td>{r.seller}</td>
                        <td>{r.origins.join(', ')}</td>
                        <td>
                          <StatusBadge tone={r.status === 'new' ? 'acc' : 'neu'}>
                            {r.status === 'new'
                              ? result.applied
                                ? 'Založeno'
                                : 'Nový'
                              : 'Už existuje'}
                          </StatusBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </Panel>

      <Panel
        title="Nasazení na weby přes Google Tag Manager"
        subtitle="Jeden kontejner pro všechny vaše weby. Skript sám pozná web podle domény, takže se na každém webu nic neupravuje."
        action={
          <button type="button" className="btn" onClick={copy}>
            {copied ? 'Zkopírováno' : 'Kopírovat'}
          </button>
        }
      >
        <pre className="scroll web-snippet" data-testid="gtm-snippet">
          {gtm(base)}
        </pre>
        <p className="note">
          Měření se spouští až po souhlasu návštěvníka. Tag „Hub – souhlas“ se musí spustit i na
          dalších stránkách, kde už je souhlas uložený (nástroj pro souhlasy to obvykle řeší
          událostí). Weby, které nejsou vaše, takto měřit nelze; jejich návštěvy uvidíte až z Google
          Analytics.
        </p>
      </Panel>
    </>
  );
}
